import { flattenConnection } from '@shopify/hydrogen-react'
import get from 'lodash/get'

import { ExtendedProductHit, ProductHit } from '../../types'
import { parseGID } from '@/utils/parseGID'
import { Metafield } from '@shopify/hydrogen-react/storefront-api-types'
import {
  GetProductDataResponseNode,
  ProductQueryData,
} from '../../queries/getProductData'
import { mapStorefrontOptionLabelOverride } from '../mapStorefrontProductOptionLabel/mapStorefrontProductOptionLabel'

/**
 * This function constructs an extended product object given the specific properties.
 * It mainly uses the input properties, but falls back to default values for certain properties.
 * This function is used when mapping search results from Algolia and combining with the storefront API.
 *
 * @param {ProductHit} hit - Hit returned from algolia.
 * @param {ResponseNode} node - Node returned from the storefront API.
 * @returns {ExtendedProductHit} - An extendeds product object.
 */
export const createStorefrontExtendedProductHit = (
  hit: ProductHit,
  node?: GetProductDataResponseNode,
): ExtendedProductHit => {
  const getMetaValue = <T>(key: string, fallback: T | undefined) =>
    get(hit.meta?.lounge, key) ?? fallback

  const images = node
    ? flattenConnection(node.images).map((image) => ({
        src: image.url,
        alt: image.altText ?? hit.title,
      }))
    : [{ src: hit.product_image, alt: hit.title }]

  /**
   * Prefer the most up-to-date values coming from the Storefront API node.
   * If a node isn't provided (e.g. not fetched yet) we fall back to the
   * values coming from Algolia so we can still render a usable placeholder.
   */

  // Price & compare-at helpers – copied from formatNode for parity
  const getPriceValueFromNode = (
    metafieldKey: keyof ProductQueryData,
    fallback: string,
  ) => {
    let value = fallback
    const metafieldValue = (node?.[metafieldKey] as Metafield | undefined)
      ?.value
    if (metafieldValue) {
      value = JSON.parse(metafieldValue).amount
    }
    return Number(value)
  }

  const price =
    node && node.priceRange?.maxVariantPrice?.amount
      ? getPriceValueFromNode(
          'priceMetafield',
          node.priceRange.maxVariantPrice.amount,
        ) * 100
      : Number(getMetaValue('price.amount', hit.price)) * 100

  const compareAtPriceRaw =
    node && node.compareAtPriceRange?.maxVariantPrice?.amount
      ? getPriceValueFromNode(
          'compareAtPriceMetafield',
          node.compareAtPriceRange.maxVariantPrice.amount,
        )
      : getMetaValue('price_compare_at.amount', hit.compare_at_price)

  const price_compare_at =
    typeof compareAtPriceRaw !== 'undefined' && compareAtPriceRaw !== null
      ? compareAtPriceRaw * 100
      : undefined

  const variants_min_price =
    node && node.priceRange?.minVariantPrice?.amount
      ? Number(node.priceRange.minVariantPrice.amount)
      : hit.variants_min_price

  const variants_max_price =
    node && node.priceRange?.maxVariantPrice?.amount
      ? Number(node.priceRange.maxVariantPrice.amount)
      : hit.variants_max_price

  const badges =
    node && node.badges?.value
      ? node.badges?.value
        ? (JSON.parse(node.badges.value) as string[])
        : []
      : (hit.meta?.lounge?.badges ?? [])

  const nodeStopSellingQuantity = node?.stopSellingQuantity?.value

  const parseStopSellingQuantity = (value: unknown): number | undefined => {
    if (value === null || typeof value === 'undefined') {
      return undefined
    }
    const parsed = typeof value === 'number' ? value : Number(value)
    return Number.isNaN(parsed) ? undefined : parsed
  }

  const stop_selling_quantity: number | undefined =
    parseStopSellingQuantity(nodeStopSellingQuantity) ??
    parseStopSellingQuantity(getMetaValue('stop_selling_quantity', undefined))

  return {
    badges,
    color: getMetaValue('color', undefined),
    canonical_color: getMetaValue('canonical_color', undefined),
    handle: hit.handle,
    href: `/products/${hit.handle}`,
    id: hit.id,
    images: images,
    isFavourited: false,
    linkedBottoms:
      node?.linkedBottoms?.references.nodes.map((node) =>
        formatNode({ node, isLinkedBottom: true }),
      ) ?? [],
    objectID: hit.objectID,
    options:
      node?.options.map((option, optionIndex) => ({
        label: mapStorefrontOptionLabelOverride({
          option,
          optionIndex,
          optionLabelOverrides: node.optionLabelOverrides,
        }),
        name: option.name,
        values: option.values,
      })) ?? [],
    position: hit.position,
    price,
    price_compare_at,
    variants_min_price,
    variants_max_price,
    stop_selling_quantity,
    queryID: hit.queryID,
    tags: hit.tags ?? [],
    title: getMetaValue('title', hit.title),
    swatches:
      node?.swatches?.references.nodes.map((node) => formatNode({ node })) ??
      [],
    isLoadingAdditionalData: !node,
    availableForSale: node?.availableForSale ?? true,
  }
}

const formatNode = ({
  node,
  isLinkedBottom = false,
}: {
  node: ProductQueryData
  isLinkedBottom?: boolean
}): Omit<ExtendedProductHit, 'swatches'> => {
  const id = parseGID(node.id).childObjectId

  const getPriceValue = (
    metafieldKey: keyof ProductQueryData,
    fallback: string,
  ) => {
    let value = fallback
    const metafieldValue = (node[metafieldKey] as Metafield)?.value
    if (metafieldValue) {
      value = JSON.parse(metafieldValue).amount
    }

    return Number(value)
  }

  const price = getPriceValue(
    'priceMetafield',
    node.priceRange.maxVariantPrice.amount,
  )

  const compareAtPrice = getPriceValue(
    'compareAtPriceMetafield',
    node.compareAtPriceRange.maxVariantPrice.amount,
  )

  return {
    id: Number(id),
    handle: node.handle,
    images: flattenConnection(node.images).map((image) => ({
      src: image.url,
      alt: image.altText ?? node.title,
    })),
    title: node.titleMetafield?.value ?? node.title,
    color: node.color?.value,
    canonical_color: node.canonicalColor?.value,
    isFavourited: false,
    href: `/products/${node.handle}`,
    objectID: id,
    options: node.options.map((option, optionIndex) => ({
      label: mapStorefrontOptionLabelOverride({
        option,
        optionIndex,
        optionLabelOverrides: node.optionLabelOverrides,
        isLinkedBottom,
      }),
      name: option.name,
      values: option.values,
    })),
    price: price * 100,
    price_compare_at:
      typeof compareAtPrice !== 'undefined' ? compareAtPrice * 100 : undefined,
    variants_min_price: Number(node.priceRange.minVariantPrice.amount),
    variants_max_price: Number(node.priceRange.maxVariantPrice.amount),
    tags: node.tags ?? [],
    badges: node.badges?.value ? JSON.parse(node.badges.value) : [],
    isLoadingAdditionalData: false,
    availableForSale: node.availableForSale,
    linkedBottoms:
      node?.linkedBottoms?.references.nodes.map((node) =>
        formatNode({ node, isLinkedBottom: true }),
      ) ?? [],
  }
}
